// Player movement + jump physics. The integration formulas are verbatim from
// v1 js/player-manager.js:646-707 and jump() at :875-907 — the `dt * 60`
// factors are intentional (they equal 1 under the fixed 1/60 step).

import type * as THREE from 'three';
import {
  LANES,
  LANE_TYPES,
  GRAVITY,
  JUMP_VELOCITY,
  DOUBLE_JUMP_FACTOR,
  FLIP_DURATION,
  LANE_LERP_RATE,
  LANE_LERP_CAP,
  GROUND_Y_PAVEMENT,
  GROUND_Y_STREET,
  AIR_RESISTANCE_COEFF,
  PLAYER_Z,
  CAR_TOP_HEIGHT,
  SLIDE,
  type LaneIndex,
} from '@/config/tuning';
import type { EventBus } from '@/app/events';
import type { Car } from '@/entities/cars';
import { buildAvatar, animateAvatar, applyCosmetics, type Avatar } from '@/player/avatar';

function groundYForLane(lane: LaneIndex): number {
  return LANE_TYPES[lane] === 'pavement' ? GROUND_Y_PAVEMENT : GROUND_Y_STREET;
}

export class Player {
  readonly avatar: Avatar;
  currentLane: LaneIndex = 0;
  targetX: number = LANES[0];
  groundY = groundYForLane(0);
  prevY = groundYForLane(0);
  isJumping = false;
  hasDoubleJumped = false;
  isFlipping = false;
  flipTime = 0;
  jumpVelocity = 0;
  isSliding = false;
  slideTimer = 0;
  standingOnCar: Car | null = null;
  private animationTime = 0;

  constructor(private readonly bus: EventBus) {
    this.avatar = buildAvatar();
    this.avatar.group.position.set(this.targetX, this.groundY, PLAYER_Z);
  }

  get object3d(): THREE.Group {
    return this.avatar.group;
  }

  get position(): THREE.Vector3 {
    return this.avatar.group.position;
  }

  /** Shop cosmetics: gi color + headband (Phase 6). */
  setCosmetics(giColor: number | null, headbandColor: number | null): void {
    applyCosmetics(this.avatar, giColor, headbandColor);
  }

  /** Belt promotion recolors the avatar's belt live (v1 updateBeltColor). */
  setBeltColor(hex: number): void {
    (this.avatar.belt.material as THREE.MeshLambertMaterial | THREE.MeshToonMaterial).color.setHex(
      hex,
    );
  }

  moveLane(direction: -1 | 1): void {
    const next = this.currentLane + direction;
    if (next < 0 || next >= LANES.length) return;
    this.currentLane = next as LaneIndex;
    this.targetX = LANES[this.currentLane];
    this.bus.emit('player:lane-changed', { lane: this.currentLane });
  }

  /** Effective collider height: slide-under checks use this. */
  get colliderHeight(): number {
    return this.isSliding ? SLIDE.COLLIDER_HEIGHT_SLIDING : SLIDE.COLLIDER_HEIGHT_STANDING;
  }

  /** Slide: ~0.7s low profile; cancellable into a jump. Ground only. */
  slide(): void {
    if (this.isJumping || this.isSliding || this.standingOnCar) return;
    this.isSliding = true;
    this.slideTimer = SLIDE.DURATION;
    this.bus.emit('player:slid', {});
  }

  private endSlide(): void {
    this.isSliding = false;
    this.slideTimer = 0;
    this.avatar.group.rotation.x = 0;
    this.avatar.group.scale.y = 1;
  }

  jump(): void {
    if (this.isSliding) this.endSlide(); // Slide cancels into jump
    if (!this.isJumping) {
      this.jumpVelocity = JUMP_VELOCITY;
      this.isJumping = true;
      this.standingOnCar = null;
      this.bus.emit('player:jumped', { double: false });
    } else if (!this.hasDoubleJumped) {
      // Double jump — slightly weaker, with a somersault flip (v1 :900-906)
      this.jumpVelocity = JUMP_VELOCITY * DOUBLE_JUMP_FACTOR;
      this.hasDoubleJumped = true;
      this.isFlipping = true;
      this.flipTime = 0;
      this.bus.emit('player:jumped', { double: true });
    }
  }

  landOnCar(car: Car): void {
    this.standingOnCar = car;
    this.isJumping = false;
    this.jumpVelocity = 0;
    this.hasDoubleJumped = false;
    this.isFlipping = false;
    this.avatar.group.rotation.x = 0;
  }

  update(dt: number, gameSpeed: number): void {
    const pos = this.avatar.group.position;
    this.prevY = pos.y;

    // Smooth lane movement (v1 :651-652)
    const lerpSpeed = Math.min(LANE_LERP_RATE * dt, LANE_LERP_CAP);
    pos.x += (this.targetX - pos.x) * lerpSpeed;

    this.groundY = groundYForLane(this.currentLane);

    // Car riding: follow the roof while the car is still underneath (v1 :661-679)
    if (this.standingOnCar) {
      const car = this.standingOnCar;
      const stillUnder =
        Math.abs(pos.z - car.group.position.z) < car.halfLength &&
        Math.abs(pos.x - car.group.position.x) < car.halfWidth + 0.3;
      if (stillUnder && car.alive) {
        pos.y = (car.topHeight || CAR_TOP_HEIGHT) + 0.24;
      } else {
        this.standingOnCar = null;
        this.isJumping = true;
        this.jumpVelocity = 0;
      }
    }

    // Jump physics (v1 :682-703)
    if (this.isJumping) {
      pos.y += this.jumpVelocity * dt * 60;
      const airResistance = 1 - Math.abs(this.jumpVelocity) * AIR_RESISTANCE_COEFF;
      this.jumpVelocity += GRAVITY * dt * 60 * airResistance;

      if (pos.y <= this.groundY) {
        pos.y = this.groundY;
        this.isJumping = false;
        this.jumpVelocity = 0;
        this.hasDoubleJumped = false;
        this.isFlipping = false;
        this.avatar.group.rotation.x = 0;
        this.bus.emit('player:landed', {});
      }
    } else if (!this.standingOnCar && pos.y !== this.groundY) {
      pos.y = this.groundY;
    }

    // Slide countdown + tucked pose
    if (this.isSliding) {
      this.slideTimer -= dt;
      this.avatar.group.rotation.x = 0.5; // Lean back
      this.avatar.group.scale.y = 0.55;
      if (this.slideTimer <= 0) this.endSlide();
    }

    // Double-jump somersault (v1 :809-817)
    if (this.isFlipping) {
      this.flipTime += dt;
      const progress = Math.min(this.flipTime / FLIP_DURATION, 1);
      this.avatar.group.rotation.x = -progress * Math.PI * 2;
      if (progress >= 1) {
        this.isFlipping = false;
        this.avatar.group.rotation.x = 0;
      }
    }

    this.animationTime += dt;
    animateAvatar(this.avatar, this.animationTime, gameSpeed, this.isJumping, this.jumpVelocity);
  }

  reset(): void {
    this.currentLane = 0;
    this.targetX = LANES[0];
    this.groundY = groundYForLane(0);
    this.isJumping = false;
    this.hasDoubleJumped = false;
    this.isFlipping = false;
    this.flipTime = 0;
    this.jumpVelocity = 0;
    this.isSliding = false;
    this.slideTimer = 0;
    this.standingOnCar = null;
    this.avatar.group.position.set(this.targetX, this.groundY, PLAYER_Z);
    this.avatar.group.rotation.set(0, 0, 0);
    this.avatar.group.scale.set(1, 1, 1);
    this.avatar.group.visible = true;
  }
}
